import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";

const escCsv = (v) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };

const downloadCsv = (rows, headers, filename) => {
  if (!rows.length) return false;
  const csv = [headers.map(h => h.label).join(","), ...rows.map(r => headers.map(h => escCsv(r[h.key])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  return true;
};

const REPORTS = [
  {
    id: "organizations", icon: "building", color: "#3b82f6", title: "Organizations",
    desc: "All orgs with plan, status, usage counts, revenue",
    headers: [
      { key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "plan", label: "Plan" },
      { key: "active", label: "Active" }, { key: "owner_phone", label: "Owner Phone" },
      { key: "prop_count", label: "Properties" }, { key: "tenant_count", label: "Tenants" },
      { key: "sp_count", label: "Service Persons" }, { key: "pay_count", label: "Payments" },
      { key: "revenue", label: "Revenue" }, { key: "created_at", label: "Created" },
    ]
  },
  {
    id: "properties", icon: "home", color: "#22c55e", title: "All Properties",
    desc: "Every property across all orgs with tenant, rent, type",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "addr", label: "Address" },
      { key: "property_type", label: "Type" }, { key: "rooms", label: "Rooms" },
      { key: "rent", label: "Rent" }, { key: "dep", label: "Deposit" },
      { key: "occ", label: "Occupied" }, { key: "tenant_name", label: "Tenant" },
      { key: "tenant_phone", label: "Tenant Phone" }, { key: "created_at", label: "Created" },
    ]
  },
  {
    id: "tenants", icon: "users", color: "#a855f7", title: "All Tenants",
    desc: "Every tenant across all orgs with property, rent, verification",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "name", label: "Name" },
      { key: "ph", label: "Phone" }, { key: "email", label: "Email" },
      { key: "property_addr", label: "Property" }, { key: "rent", label: "Rent" },
      { key: "id_type", label: "ID Type" }, { key: "verified", label: "Verified" },
      { key: "join_date", label: "Join Date" }, { key: "vac", label: "Vacate" },
    ]
  },
  {
    id: "payments", icon: "credit-card", color: "#f59e0b", title: "All Payments",
    desc: "Every payment across all orgs with status, tenant, amount",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "tenant_name", label: "Tenant" },
      { key: "tenant_phone", label: "Phone" }, { key: "property_addr", label: "Property" },
      { key: "mo", label: "Month" }, { key: "amt", label: "Rent" }, { key: "com", label: "Common" },
      { key: "st", label: "Status" }, { key: "mth", label: "Method" }, { key: "dt", label: "Date" },
    ]
  },
  {
    id: "billing", icon: "dollar", color: "#22c55e", title: "Billing Transactions",
    desc: "All Razorpay transactions with org, amount, status",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "amount", label: "Amount (paise)" },
      { key: "plan_type", label: "Plan" }, { key: "status", label: "Status" },
      { key: "razorpay_payment_id", label: "Razorpay ID" }, { key: "created_at", label: "Date" },
    ]
  },
];

export default function AdminReports() {
  const { t, h, go, flash } = useContext(Ctx);
  const [loading, setLoading] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.adminGetStats().then(r => { if (r.success) setStats(r.stats); });
  }, []);

  const handleExport = async (report) => {
    setLoading(report.id);
    const res = await api.adminReport(report.id);
    if (res.success && res.data?.length > 0) {
      const date = new Date().toISOString().slice(0, 10);
      const success = downloadCsv(res.data, report.headers, `platform_${report.id}_${date}.csv`);
      if (success) flash(`${report.title} exported! (${res.data.length} rows)`);
      else flash("Export failed", "error");
    } else {
      flash("No data to export", "error");
    }
    setLoading(null);
  };

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Back to Dashboard
    </button>

    <h2 style={{ ...type.h1, color: h.tx, margin: `0 0 ${sp.sm}px` }}>Platform Reports</h2>
    <p style={{ ...type.sm, color: h.ts, margin: `0 0 ${sp.xl}px` }}>Export platform-wide data as CSV</p>

    {/* Platform Stats */}
    {stats && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.xl }}>
      {[
        { label: "Total Revenue", value: `\u20B9${Math.round((stats.totalRevenue || 0) / 100).toLocaleString()}`, color: h.gn },
        { label: "This Month", value: `\u20B9${Math.round((stats.monthRevenue || 0) / 100).toLocaleString()}`, color: h.ac },
        { label: "Organizations", value: stats.totalOrgs, color: h.pp || h.ac },
      ].map((s, i) => (
        <div key={i} style={{ ...crd(h), padding: sp.md, textAlign: "center" }}>
          <div style={{ ...type.h3, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 9, color: h.tt, marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>}

    {/* Report Cards */}
    <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
      {REPORTS.map((r, i) => (
        <div key={r.id} onClick={() => !loading && handleExport(r)}
          style={{
            display: "flex", alignItems: "center", gap: sp.md,
            padding: `${sp.md}px ${sp.lg}px`, cursor: loading ? "default" : "pointer",
            borderBottom: i < REPORTS.length - 1 ? `1px solid ${h.bd}` : "none",
            opacity: loading === r.id ? 0.6 : 1,
          }}>
          <div style={icBox(r.color, 36)}>
            <Ic n={r.icon} s={16} c={r.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{r.title}</div>
            <div style={{ ...type.xs, color: h.tt }}>{r.desc}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
            {loading === r.id ? <span style={{ ...type.xs, color: h.ts }}>...</span> : <>
              <span style={bdg(`${r.color}15`, r.color)}>CSV</span>
              <Ic n="chevron-right" s={14} c={h.tt} />
            </>}
          </div>
        </div>
      ))}
    </div>

    {/* Quick Stats */}
    {stats && <div style={{ ...crd(h), marginTop: sp.xl }}>
      <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Platform Overview</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm }}>
        {[
          { label: "Properties", value: stats.totalProps },
          { label: "Tenants", value: stats.totalTenants },
          { label: "Payments", value: stats.totalPayments },
          { label: "Service Persons", value: stats.totalServicePersons },
          { label: "Leads", value: stats.totalLeads },
          { label: "Leases", value: stats.totalLeases },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center", padding: sp.sm }}>
            <div style={{ ...type.h3, color: h.tx }}>{s.value}</div>
            <div style={{ fontSize: 9, color: h.tt }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>}
  </div>;
}
